import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useApiClient } from "@/infrastructure/rest";
import { ContactpersoonEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { ContactpersoonDto, CreateContactpersoonRequestDto, UpdateContactpersoonRequestDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTACTPERSONEN_QUERY_KEY } from "./constants.ts";
import { Contactpersoon } from "./domain.ts";

export function useGetContactpersonen<T = Contactpersoon[]>(dossierId: string, select?: (data: Contactpersoon[]) => T) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, CONTACTPERSONEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<ContactpersoonDto[]>(`/dossiers/${dossierId}/contactpersonen`);
            const contactpersonen = data?.map((contactpersoon) => Contactpersoon.fromDto(contactpersoon)) ?? [];
            return contactpersonen.sort(Contactpersoon.sort);
        },
        select,
    });
}

export function useGetContactpersonenForTrein(dossierId: string, treinNummer: number) {
    return useGetContactpersonen(dossierId, (data) => {
        return data.filter((contact) => contact.treinnummer === treinNummer);
    });
}

export type PostContactpersoon = CreateContactpersoonRequestDto & {
    dossierId: string;
};

export function usePostContactpersoon() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...data }: PostContactpersoon) => {
            try {
                const result = await apiClient.post<ContactpersoonDto>(`/dossiers/${dossierId}/contactpersonen`, data);
                if (!result) {
                    throw new Error(`Response from 'POST /dossiers/${dossierId}/contactpersonen' was empty`);
                }
                return result;
            } catch (e) {
                console.error("Error posting contactpersoon:", e);
                throw e;
            }
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting contactpersoon:", error);
        },
    });
}

export type PatchContactpersoon = UpdateContactpersoonRequestDto & {
    dossierId: string;
    contactpersoonId: number;
};

export function usePatchContactpersoon() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, contactpersoonId, ...data }: PatchContactpersoon) => {
            await apiClient.patch<ContactpersoonDto>(`/dossiers/${dossierId}/contactpersonen/${contactpersoonId}`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error patching contactpersoon:", error);
        },
    });
}

type DeleteContactpersoon = {
    dossierId: string;
    contactpersoonId: number;
};

export function useDeleteContactpersoon() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, contactpersoonId }: DeleteContactpersoon) => {
            await apiClient.delete<ContactpersoonDto>(`/dossiers/${dossierId}/contactpersonen/${contactpersoonId}`);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error, variables) => {
            console.error(`Error deleting contactpersoon with id ${variables.contactpersoonId} for dossier ${variables.dossierId}:`, error);
        },
    });
}

export function useRealtimeContactpersonen() {
    const { handleEvent } = useUpdateDossierPropertyQueriesData("contactpersonen");

    useRealtimeTopic<ContactpersoonEvent>("contactpersonen", {
        onMessage: (message) => {
            const { contactpersoon, dossierId, reason } = message;
            handleEvent(dossierId, contactpersoon.id, reason, Contactpersoon.fromDto(contactpersoon), Contactpersoon.sort);
        },
    });
}
